import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Job, JobCreator } from '../../models/Job';
import { fetchJobById, fetchSimilarJobs, checkBackendStatus } from '../../services/jobService';
import { saveJob, unsaveJob, isJobSaved } from '../../services/savedJobsService';
import { submitApplication, checkApplicationStatus } from '../../services/applicationService';
import { useUserStore } from '../../store/useUserStore';
import { useAppStore } from '../../store/useAppStore';
import { LoginPromptDialog, SheetLoading } from '../../components/shared/ui';
import { ApplicationForm, ApplicationData } from '../../components/jobs/details/ApplicationForm';
import { useAuth } from '../../provider/AuthProvider';
import { ROUTE_NAMES } from '../../routes/routeConstants';
import { useLoginModal } from '../../context/LoginModalContext';
import { AcaIcon } from '../../components/shared/aca';
import SheetGround from '../../components/shared/ui/SheetGround';
import { VELLUM as T, SANS, MONO, mono, btn } from '../../components/shared/folio/kit';
import { JobDetailHeader } from '../../components/sections/JobDetailHeader';
import { JobDetailBody } from '../../components/sections/JobDetailBody';
import { JobDetailSidebar } from '../../components/sections/JobDetailSidebar';

/* The folio vocabulary under the token names these files already used. */
const pal = {
  paper: T.ground,
  paperDeep: T.ground,
  card: T.ground,
  ink: T.ink,
  inkSoft: T.mute,
  inkMute: T.mute,
  rule: T.rule,
  ruleSoft: T.rule,
  accent: T.accent,
  onAccent: T.onAccent,
};
const fam = { sans: SANS, mono: MONO, serif: SANS };

/* Every section of the dossier opened with the same twenty lines of inline
   style. One head, declared once, so the sections cannot drift apart. */
const SectionHead: React.FC<{ num: string; eyebrow: string; title: string }> = ({
  num,
  eyebrow,
  title,
}) => (
  <div style={{ borderBottom: `1px solid ${pal.rule}`, paddingBottom: 14, marginBottom: 24 }}>
    <div style={{ ...mono(T), fontSize: 10.5, letterSpacing: '0.22em' }}>
      § {num} · {eyebrow}
    </div>
    <h2
      style={{
        fontFamily: fam.sans,
        fontSize: 28,
        fontWeight: 600,
        letterSpacing: '-0.015em',
        margin: '6px 0 0',
        color: pal.ink,
      }}
    >
      {title}
    </h2>
  </div>
);

const RuleList: React.FC<{ items: string[]; marker: string; markerColor: string }> = ({
  items,
  marker,
  markerColor,
}) => (
  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 12 }}>
    {items.map((item, i) => (
      <li
        key={i}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 14,
          fontFamily: fam.sans,
          fontSize: 16,
          lineHeight: 1.6,
          color: pal.inkSoft,
        }}
      >
        <span
          aria-hidden
          style={{
            fontFamily: fam.mono,
            fontSize: 12,
            color: markerColor,
            flexShrink: 0,
            marginTop: 3,
            letterSpacing: '0.04em',
          }}
        >
          {marker}
        </span>
        {item}
      </li>
    ))}
  </ul>
);

const formatDay = (value?: string | null): string | null => {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const JobDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('');
  const [similarJobs, setSimilarJobs] = useState<{ id: string; title: string; institution: string; location: string }[]>([]);
  const { toggleSavedJob } = useUserStore();
  const addToast = useAppStore((state) => state.addToast);
  const { openLoginModal } = useLoginModal();

  // Handle back navigation
  const handleBackToJobs = () => {
    navigate(ROUTE_NAMES.PAGES.JOBS);
  };

  // Helper: get employer data from createdBy
  const getEmployerData = (): JobCreator | null => {
    if (!job?.createdBy || typeof job.createdBy === 'string') return null;
    return job.createdBy as JobCreator;
  };

  const getInstitutionTypeLabel = (): string => {
    const employer = getEmployerData();
    if (!employer?.institutionType) return 'Institution';
    const labels: Record<string, string> = {
      university: 'University',
      college: 'College',
      'research-institute': 'Research Institute',
      other: 'Institution',
    };
    return labels[employer.institutionType] || 'Institution';
  };

  const getInstitutionDescription = (): string => {
    const employer = getEmployerData();
    if (employer?.institutionDescription) return employer.institutionDescription;
    return `${job?.company} is committed to excellence and innovation. We provide a dynamic and supportive environment for our employees to grow and succeed.`;
  };

  const getInstitutionCulture = (): string[] => {
    const employer = getEmployerData();
    if (employer?.institutionCulture && employer.institutionCulture.length > 0) {
      return employer.institutionCulture;
    }
    return [];
  };

  // Handle save/unsave job
  const handleSaveJob = async () => {
    if (!job) return;
    try {
      if (isSaved) {
        await unsaveJob(job.id);
        toggleSavedJob(job.id);
        setIsSaved(false);
      } else {
        await saveJob(job.id);
        toggleSavedJob(job.id);
        setIsSaved(true);
      }
    } catch (err) {
      console.error('Error saving/unsaving job:', err);
    }
  };

  /* A post with a passed deadline is a record, not an opportunity. The
     register knows the date, so the page must not let a scholar spend an
     application on a closing that has already happened. */
  const closesOn = job?.applicationDeadline
    ? new Date(job.applicationDeadline)
    : null;
  const closesValid = !!(closesOn && !isNaN(closesOn.getTime()));
  const closesLabel = closesValid
    ? closesOn!.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;
  const applicationsClosed = !!(closesValid && closesOn!.getTime() < Date.now());
  /* Counted from the start of today, so "1 day left" does not become
     "0 days" purely because the visitor loaded the page in the evening. */
  const daysLeft = closesValid
    ? Math.ceil((closesOn!.getTime() - new Date().setHours(0, 0, 0, 0)) / 86400000)
    : null;
  const postedLabel = formatDay(job?.postedDate);

  const handleApplyClick = () => {
    setShowApplicationForm(true);
  };

  const handleApplicationSubmit = async (formData: ApplicationData) => {
    if (!job) return;
    try {
      await submitApplication({
        jobId: job.id,
        coverLetter: formData.coverLetter,
        resumeUrl: formData.resumeUrl || undefined,
        availability: formData.availability as 'Immediately' | '1 month' | '2 months' | '3 months',
        expectedSalary: formData.expectedSalary || undefined,
        additionalInfo: formData.additionalInfo || undefined,
      });
      setApplicationSubmitted(true);
      setHasApplied(true);
      setShowApplicationForm(false);
      setTimeout(() => { setApplicationSubmitted(false); }, 5000);
    } catch (err: any) {
      throw err;
    }
  };

  /* The contents of the dossier, in the order they are filed. Sections a
     post does not carry are simply not filed, and the numbering closes up
     behind them rather than leaving a gap in the sequence. */
  const sections = useMemo(() => {
    if (!job) return [] as { id: string; label: string }[];
    const list = [{ id: 'role', label: 'The post' }];
    if (job.requirements && job.requirements.length > 0) list.push({ id: 'requirements', label: 'Requirements' });
    if (job.responsibilities && job.responsibilities.length > 0) list.push({ id: 'responsibilities', label: 'Responsibilities' });
    if (job.benefits && job.benefits.length > 0) list.push({ id: 'benefits', label: 'Benefits' });
    list.push({ id: 'institution', label: 'The institution' });
    return list;
  }, [job]);
  const sectionKey = sections.map((s) => s.id).join('|');
  const numberOf = (sectionId: string): string =>
    String(sections.findIndex((s) => s.id === sectionId) + 1).padStart(2, '0');

  useEffect(() => {
    let cancelled = false;
    const loadJob = async () => {
      try {
        setLoading(true);
        setError(null);

        await checkBackendStatus();
        if (cancelled) return;

        const foundJob = await fetchJobById(id!);
        if (cancelled) return;

        if (foundJob) {
          setJob(foundJob);

          // Fetch similar jobs — guard against unmount
          fetchSimilarJobs(foundJob.id)
            .then((jobs) => {
              if (cancelled) return;
              setSimilarJobs(
                jobs.map((j) => ({
                  id: j.id,
                  title: j.title,
                  institution: j.company,
                  location: j.location,
                }))
              );
            })
            .catch(() => { /* swallow — request was cancelled or failed */ });

          if (user) {
            const savedStatus = await isJobSaved(foundJob.id);
            if (cancelled) return;
            setIsSaved(savedStatus);

            // Only employees can apply — skip the applied-check for employers
            // (owners of listings and other employers alike).
            if (user.role !== 'employer') {
              const applicationStatus = await checkApplicationStatus(foundJob.id);
              if (cancelled) return;
              setHasApplied(applicationStatus.hasApplied);
            }
          }
        } else {
          setError('This post is not in the register.');
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load job:', err);
        setError('The post could not be loaded. Try again in a moment.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (id) {
      loadJob();
    } else {
      setError('That link does not point to a post in the register.');
      setLoading(false);
    }
    return () => { cancelled = true; };
  }, [id, user]);

  /* Guests should never reach this page — redirect to the listing and
     surface the login modal.

     WAIT FOR AUTH TO RESOLVE FIRST. The provider starts every page load at
     user: null, loading: true, so testing !user alone is true on the first
     render for a signed-in reader too - which bounced them to /jobs and
     flashed the login dialog before their own session had finished loading.
     The session restore is three round trips (me -> refresh -> me), so that
     window is long enough to see. /jobs guards the same redirect this way.

     The `cancelled` flag this replaces did nothing: it was only ever set in
     the cleanup, so `!cancelled` was true every time the body ran. */
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(ROUTE_NAMES.PAGES.JOBS, { replace: true });
      openLoginModal();
    }
  }, [user, authLoading, navigate, openLoginModal]);

  /* The index marks where the reader actually is. The bottom margin keeps
     the mark from jumping to a section that has only just crept into view
     at the foot of the window. */
  useEffect(() => {
    if (!sectionKey) return;
    const ids = sectionKey.split('|');
    const nodes = ids
      .map((sectionId) => document.getElementById(`jd-sec-${sectionId}`))
      .filter((node): node is HTMLElement => !!node);
    if (nodes.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const onscreen = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (onscreen.length > 0) {
          setActiveSection(onscreen[0].target.id.replace('jd-sec-', ''));
        }
      },
      { rootMargin: '-120px 0px -55% 0px', threshold: 0 }
    );
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [sectionKey]);

  const scrollToSection = (sectionId: string) => {
    const el = document.getElementById(`jd-sec-${sectionId}`);
    if (!el) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.scrollY - 104,
      behavior: reduced ? 'auto' : 'smooth',
    });
  };

  const handleShareJob = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: job?.title,
          text: `Check out this job: ${job?.title} at ${job?.company}`,
          url: window.location.href,
        });
        addToast({ type: 'success', title: 'Shared', message: 'Post shared.', duration: 3000 });
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        navigator.clipboard.writeText(window.location.href);
        addToast({ type: 'info', title: 'Link copied', message: 'The link to this post is on your clipboard.', duration: 3000 });
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      addToast({ type: 'success', title: 'Link copied', message: 'The link to this post is on your clipboard.', duration: 3000 });
    }
  };

  // Loading state
  if (loading) {
    return <SheetLoading text="Loading the post…" />;
  }

  // Error state — still the same sheet, so still ruled like one.
  if (error || !job) {
    return (
      <SheetGround
        style={{
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 24,
          fontFamily: fam.sans,
          textAlign: 'center',
          padding: '40px 20px',
        }}
      >
        <div
          style={{
            fontFamily: fam.mono,
            fontSize: 10.5,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: pal.accent,
          }}
        >
          § Position not found
        </div>
        <h1
          style={{
            fontFamily: fam.sans,
            fontSize: 'clamp(32px, 5vw, 56px)',
            fontWeight: 600,
            letterSpacing: '-0.03em',
            lineHeight: 1,
            color: pal.ink,
            margin: 0,
          }}
        >
          This post is not in the register.
        </h1>
        <p
          style={{
            fontFamily: fam.serif,
            fontSize: 17,
            color: pal.inkSoft,
            maxWidth: 480,
            margin: 0,
          }}
        >
          It may have been withdrawn by the institution, or the link may be wrong.
        </p>
        <button type="button" onClick={() => navigate(ROUTE_NAMES.PAGES.JOBS)} className="jd-accent" style={btn(T, "accent")}>
          Back to the register
        </button>
      </SheetGround>
    );
  }

  // Guests are redirected away by the effect above — render nothing while navigation runs.
  if (!user) return null;

  const showApply = user?.role !== 'employer';
  const employerForOwnerCheck = getEmployerData();
  const isOwner =
    !!user && !!employerForOwnerCheck && user.id === employerForOwnerCheck._id;
  const cultureTags = getInstitutionCulture();

  /* Where, how, and how long. These qualify the post before a word of the
     prose is read, so they are set on the masthead - and, having been set
     there, they are deliberately absent from the particulars below. */
  const spine = [job.location, job.type, job.employmentType].filter(Boolean) as string[];

  /* The academic filing, in the order copy.ts uses for the register's own
     anatomy: subfield first, because it is the unit of relevance. Salary,
     the closing date and the spine are not repeated here - they belong to
     the act, and the act has its own block. */
  const filing = [
    { label: 'Subfield', value: job.subject || job.subjectCategory },
    { label: 'Study levels', value: (job.studyLevels || []).join(' · ') },
    { label: 'Academic role', value: (job.academicRoles || []).join(' · ') },
    { label: 'Category', value: job.jobCategory },
    { label: 'Contract', value: job.contractDuration },
    { label: 'Career level', value: job.careerLevel === 'None' ? '' : job.careerLevel },
    { label: 'Experience', value: job.experience },
    { label: 'Starts', value: formatDay(job.startDate) || job.startDate },
  ].filter((item) => !!item.value);

  /* Two rows of three is as much of a detour as the foot of a dossier can
     carry before it stops being a pointer and becomes a second listing. */
  const nearby = similarJobs.slice(0, 6);
  const terminalNum = String(sections.length + 1).padStart(2, '0');

  return (
    <SheetGround className="jd-root" style={{ color: pal.ink, fontFamily: fam.sans, minHeight: '100vh' }}>

      {/* Application submitted banner */}
      {applicationSubmitted && (
        <div
          role="status"
          style={{
            background: pal.card,
            borderBottom: `1px solid ${pal.rule}`,
          }}
        >
          <div
            style={{
              maxWidth: 1280,
              margin: '0 auto',
              padding: '14px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <AcaIcon name="check" size={15} stroke={pal.ink} />
            <span style={{ ...mono(T, T.ink), letterSpacing: '0.18em' }}>
              Application submitted. Track it in your dashboard.
            </span>
          </div>
        </div>
      )}

      {/* Breadcrumb */}
      <section style={{ borderBottom: `1px solid ${pal.rule}` }}>
        <div
          style={{
            maxWidth: 1280,
            margin: '0 auto',
            padding: '14px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            ...mono(T),
            fontSize: 10,
            letterSpacing: '0.18em',
          }}
        >
          <button
            onClick={handleBackToJobs}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              ...mono(T),
              fontSize: 10,
              letterSpacing: '0.18em',
              padding: 0,
            }}
          >
            ← The Register
          </button>
          <span style={{ color: pal.rule }}>/</span>
          <span style={{ color: pal.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}>
            {job.title}
          </span>
        </div>
      </section>

      {/* Masthead — the post, the institution, and the act, side by side */}
      <section style={{ borderBottom: `1px solid ${pal.rule}` }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '44px 20px 44px' }}>
          <JobDetailHeader
            title={job.title}
            institution={job.company}
            institutionType={getInstitutionTypeLabel()}
            logoUrl={job.logoUrl}
            isHot={job.hot}
            isSaved={isSaved}
            hasApplied={hasApplied}
            showApply={showApply}
            isOwner={isOwner}
            applicationsClosed={applicationsClosed}
            salary={job.salary}
            closesLabel={closesLabel}
            daysLeft={daysLeft}
            postedLabel={postedLabel}
            spine={spine}
            onSaveToggle={handleSaveJob}
            onShare={handleShareJob}
            onApply={handleApplyClick}
          />
        </div>
      </section>

      {/* The cover sheet.

          The register's claim is that a listing carries what academic
          hiring actually turns on - subfield, level, role, contract. So
          the dossier opens on exactly those, filed and legible, rather
          than tucking them into a card beside the prose. */}
      {filing.length > 0 && (
        <section style={{ borderBottom: `1px solid ${pal.rule}` }}>
          <div style={{ maxWidth: 1280, margin: '0 auto', padding: '36px 20px 40px' }}>
            <div className="jd-cover" style={{ border: `1.5px solid ${pal.ink}`, background: pal.paper }}>
              <div
                style={{
                  ...mono(T, T.accent),
                  padding: '11px 16px',
                  borderBottom: `1px solid ${pal.rule}`,
                }}
              >
                § Filed as
              </div>
              <dl className="jd-cover-grid" style={{ margin: 0, display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
                {filing.map((item) => (
                  <div key={item.label} className="jd-cover-cell" style={{ padding: '14px 16px' }}>
                    <dt style={{ ...mono(T), fontSize: 10, letterSpacing: '0.2em', marginBottom: 7 }}>{item.label}</dt>
                    <dd
                      style={{
                        margin: 0,
                        fontFamily: MONO,
                        fontSize: 14,
                        lineHeight: 1.35,
                        color: pal.ink,
                      }}
                    >
                      {item.value}
                    </dd>
                  </div>
                ))}
              </dl>

              {job.tags && job.tags.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 16,
                    flexWrap: 'wrap',
                    padding: '13px 16px',
                    borderTop: `1px solid ${pal.rule}`,
                  }}
                >
                  <span style={{ ...mono(T), fontSize: 10, letterSpacing: '0.2em' }}>Keywords</span>
                  <span style={{ fontFamily: MONO, fontSize: 13, color: pal.inkSoft }}>
                    {job.tags.join('  ·  ')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* The enclosed matter, with the sheet index in the left margin */}
      <section>
        <div
          className="jd-body-grid"
          style={{
            maxWidth: 1280,
            margin: '0 auto',
            padding: '52px 20px 84px',
            display: 'grid',
            gridTemplateColumns: '196px minmax(0, 1fr)',
            gap: 56,
            alignItems: 'start',
          }}
        >
          <JobDetailSidebar>
            <div style={{ ...mono(T, T.accent), marginBottom: 14 }} className="jd-index-head">
              § Contents
            </div>
            <ol className="jd-index-list" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {sections.map((section, i) => {
                const active = activeSection === section.id;
                return (
                  <li key={section.id} className="jd-index-item" style={{ borderTop: `1px solid ${pal.rule}` }}>
                    <button
                      type="button"
                      onClick={() => scrollToSection(section.id)}
                      aria-current={active ? 'true' : undefined}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        background: 'transparent',
                        border: 'none',
                        borderLeft: `2px solid ${active ? pal.accent : 'transparent'}`,
                        padding: '11px 0 11px 12px',
                        cursor: 'pointer',
                        ...mono(T),
                        fontSize: 10,
                        letterSpacing: '0.16em',
                        color: active ? pal.ink : pal.inkSoft,
                      }}
                    >
                      <span style={{ color: active ? pal.accent : pal.inkMute, marginRight: 8 }}>
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      {section.label}
                    </button>
                  </li>
                );
              })}
            </ol>

            {showApply && !applicationsClosed && !hasApplied && !isOwner && (
              <button
                type="button"
                onClick={handleApplyClick}
                className="jd-index-apply"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  marginTop: 22,
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  ...mono(T, T.accent),
                  fontSize: 10,
                  letterSpacing: '0.18em',
                  borderBottom: `1px solid ${pal.accent}`,
                  paddingBottom: 3,
                }}
              >
                Apply →
              </button>
            )}
          </JobDetailSidebar>

          <JobDetailBody>
            {/* Role */}
            <div id="jd-sec-role">
              <SectionHead num={numberOf('role')} eyebrow="Role" title="About the position." />
              <p
                style={{
                  fontFamily: fam.sans,
                  fontSize: 17,
                  lineHeight: 1.65,
                  color: pal.inkSoft,
                  margin: 0,
                  whiteSpace: 'pre-line',
                }}
              >
                {job.description}
              </p>
            </div>

            {/* Requirements */}
            {job.requirements && job.requirements.length > 0 && (
              <div id="jd-sec-requirements">
                <SectionHead
                  num={numberOf('requirements')}
                  eyebrow="Requirements"
                  title="What the post requires."
                />
                <RuleList items={job.requirements} marker="—" markerColor={pal.rule} />
              </div>
            )}

            {/* Responsibilities */}
            {job.responsibilities && job.responsibilities.length > 0 && (
              <div id="jd-sec-responsibilities">
                <SectionHead
                  num={numberOf('responsibilities')}
                  eyebrow="Responsibilities"
                  title="Key responsibilities."
                />
                <RuleList items={job.responsibilities} marker="§" markerColor={pal.accent} />
              </div>
            )}

            {/* Benefits — carried by the record all along, never set until now */}
            {job.benefits && job.benefits.length > 0 && (
              <div id="jd-sec-benefits">
                <SectionHead
                  num={numberOf('benefits')}
                  eyebrow="Benefits"
                  title="What the post offers."
                />
                <RuleList items={job.benefits} marker="+" markerColor={pal.rule} />
              </div>
            )}

            {/* The institution */}
            <div id="jd-sec-institution">
              <SectionHead
                num={numberOf('institution')}
                eyebrow={getInstitutionTypeLabel()}
                title={`About ${job.company}.`}
              />

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: cultureTags.length > 0 ? '1fr 1fr' : '1fr',
                  gap: 32,
                }}
                className="jd-institution-grid"
              >
                <div>
                  <div style={{ ...mono(T), fontSize: 10, letterSpacing: '0.18em', marginBottom: 10 }}>
                    Overview
                  </div>
                  <p
                    style={{
                      fontFamily: fam.sans,
                      fontSize: 15,
                      lineHeight: 1.65,
                      color: pal.inkSoft,
                      margin: 0,
                    }}
                  >
                    {getInstitutionDescription()}
                  </p>
                </div>
                {cultureTags.length > 0 && (
                  <div>
                    <div style={{ ...mono(T), fontSize: 10, letterSpacing: '0.18em', marginBottom: 10 }}>
                      Culture
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0, borderTop: `1px solid ${pal.rule}`, borderLeft: `1px solid ${pal.rule}` }}>
                      {cultureTags.map((tag, i) => (
                        <span
                          key={i}
                          style={{
                            display: 'inline-block',
                            padding: '6px 12px',
                            borderRight: `1px solid ${pal.rule}`,
                            borderBottom: `1px solid ${pal.rule}`,
                            ...mono(T),
                            fontSize: 10,
                            letterSpacing: '0.16em',
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* The dossier's terminal act.

                You finish the enclosed matter and the action is where you
                finish, under a heavy rule that closes the file. The
                masthead keeps its own apply so the task is never out of
                reach - an Operate surface may not make the visitor scroll
                back up to act. */}
            {showApply && !isOwner && (
              <div style={{ borderTop: `1.5px solid ${pal.ink}`, paddingTop: 26 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
                  <span style={mono(T, T.accent)}>§ {terminalNum} · Application</span>
                  <span style={mono(T)}>
                    {applicationsClosed
                      ? 'Applications have closed'
                      : hasApplied
                      ? 'Application on file'
                      : closesLabel
                      ? `Closes ${closesLabel}`
                      : 'Ready to apply?'}
                  </span>
                </div>
                {!hasApplied && !applicationsClosed && (
                  <button
                    type="button"
                    onClick={handleApplyClick}
                    className="jd-accent"
                    style={{ ...btn(T, 'accent'), marginTop: 18 }}
                  >
                    Apply for this position
                  </button>
                )}
              </div>
            )}
          </JobDetailBody>
        </div>
      </section>

      {/* Elsewhere in the register.

          These were stacked in a 320px rail beside the prose, where three
          entries filled the column and a fourth ran off the bottom of the
          sheet. Full width, ruled into a grid, they read as what they are:
          the next thing to look at once this post is decided. */}
      {similarJobs.length > 0 && (
        <section style={{ borderTop: `1px solid ${pal.rule}` }}>
          <div style={{ maxWidth: 1280, margin: '0 auto', padding: '48px 20px 72px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 20,
                flexWrap: 'wrap',
                borderBottom: `1.5px solid ${pal.ink}`,
                paddingBottom: 12,
              }}
            >
              <h2
                style={{
                  fontFamily: fam.sans,
                  fontSize: 28,
                  fontWeight: 600,
                  letterSpacing: '-0.015em',
                  margin: 0,
                  color: pal.ink,
                }}
              >
                Elsewhere in the register.
              </h2>
              <span style={mono(T, T.accent)}>§ Similar positions</span>
            </div>

            <div className="jd-similar-clip">
            <div className="jd-similar-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
              {nearby.map((sj) => (
                <button
                  key={sj.id}
                  type="button"
                  onClick={() => navigate(`/jobs/${sj.id}`)}
                  className="jd-similar-cell"
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    background: 'transparent',
                    border: 'none',
                    padding: '20px 20px 22px',
                    cursor: 'pointer',
                  }}
                >
                  <div
                    className="jd-similar-title"
                    style={{
                      fontFamily: fam.sans,
                      fontSize: 17,
                      fontWeight: 600,
                      letterSpacing: '-0.015em',
                      color: pal.ink,
                      marginBottom: 6,
                      lineHeight: 1.25,
                    }}
                  >
                    {sj.title}
                  </div>
                  <div style={{ fontFamily: fam.sans, fontSize: 14, color: pal.inkSoft, marginBottom: 10 }}>
                    {sj.institution}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                      ...mono(T),
                      fontSize: 9.5,
                      letterSpacing: '0.14em',
                      color: pal.inkMute,
                    }}
                  >
                    <AcaIcon name="pin" size={10} stroke={pal.inkMute} />
                    {sj.location}
                  </div>
                </button>
              ))}
            </div>
            </div>

            <button
              onClick={() => navigate(ROUTE_NAMES.PAGES.JOBS)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                marginTop: 24,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                ...mono(T),
                fontSize: 10,
                letterSpacing: '0.18em',
                borderBottom: `1px solid ${pal.rule}`,
                padding: '0 0 3px',
              }}
            >
              View all positions →
            </button>
          </div>
        </section>
      )}

      {/* Mobile apply bar — only for job seekers */}
      {showApply && !applicationsClosed && !isOwner && (
        <div
          className="jd-mobile-apply"
          style={{
            display: 'none',
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            background: pal.paper,
            borderTop: `1.5px solid ${pal.ink}`,
            padding: '12px 20px',
            zIndex: 40,
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
          }}
        >
          <div style={{ minWidth: 0 }}>
            {job.salary && (
              <div
                style={{
                  fontFamily: fam.sans,
                  fontSize: 16,
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                  color: pal.ink,
                }}
              >
                {job.salary}
              </div>
            )}
            <div
              style={{
                ...mono(T),
                fontSize: 9.5,
                letterSpacing: '0.14em',
                color: typeof daysLeft === 'number' && daysLeft <= 14 ? pal.accent : pal.inkMute,
              }}
            >
              {hasApplied
                ? 'Application on file'
                : typeof daysLeft === 'number' && daysLeft >= 0
                ? daysLeft === 0
                  ? 'Closes today'
                  : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`
                : 'Open'}
            </div>
          </div>
          {hasApplied ? (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                ...mono(T),
                fontSize: 10,
                letterSpacing: '0.18em',
                border: `1px solid ${pal.rule}`,
                padding: '10px 14px',
                flexShrink: 0,
              }}
            >
              <AcaIcon name="check" size={12} stroke={pal.inkSoft} />
              On file
            </div>
          ) : (
            <button type="button" onClick={handleApplyClick} className="jd-accent" style={{ ...btn(T, "accent"), flexShrink: 0 }}>
              Apply
            </button>
          )}
        </div>
      )}

      {/* Login Prompt Dialog */}
      <LoginPromptDialog
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        title="Sign in to continue"
        message="Posts in the register are open to signed-in scholars. Sign in, or take an account, to read this one."
      />

      {/* Application Form Modal */}
      {showApplicationForm && job && (
        <ApplicationForm
          job={job}
          onSubmit={handleApplicationSubmit}
          onCancel={() => setShowApplicationForm(false)}
          hasAlreadyApplied={hasApplied}
          userCvUrl={user?.cvUrl}
        />
      )}

      <style>{`
        /* The cover sheet is ruled into cells, not boxed as tiles: one
           hairline between neighbours, and never one doubled against the
           sheet's own border.

           EVERY cell rules its right and bottom edge, with no exception
           for the last column or the last row. The grid is then hung 1px
           past its container on those two sides and the container clips
           it, so the outermost rules land exactly under the sheet's own
           border instead of beside it. Done with :nth-child(4n) this has
           to be restated at every breakpoint and silently goes wrong the
           moment the column count changes or a row comes up short; done
           by clipping, the column count is simply not part of it. */
        .jd-cover { overflow: hidden; }
        .jd-cover-grid { margin-right: -1px; margin-bottom: -1px; }
        .jd-cover-cell { border-right: 1px solid ${pal.rule}; border-bottom: 1px solid ${pal.rule}; }

        .jd-similar-clip { overflow: hidden; border-bottom: 1px solid ${pal.rule}; }
        .jd-similar-grid { margin-right: -1px; margin-bottom: -1px; }
        .jd-similar-cell { border-bottom: 1px solid ${pal.rule}; border-right: 1px solid ${pal.rule}; transition: background 150ms ease; }
        button.jd-similar-cell:hover { background: ${pal.card}; }
        button.jd-similar-cell:hover .jd-similar-title { color: ${pal.accent}; }

        .jd-cell-btn { transition: background 150ms ease; }
        .jd-cell-btn:hover { background: ${pal.card}; }
        .jd-index-item button { transition: color 150ms ease, border-color 150ms ease; }
        .jd-index-item button:hover { color: ${pal.accent}; }

        .jd-accent { transition: background 150ms ease; }
        .jd-accent:hover { background: ${T.accentDeep}; }
        .jd-root button:focus-visible,
        .jd-root a:focus-visible,
        .jd-root input:focus-visible,
        .jd-root select:focus-visible,
        .jd-root textarea:focus-visible {
          outline: 2px solid ${pal.accent}; outline-offset: 3px;
        }
        .jd-root input::placeholder, .jd-root textarea::placeholder { color: ${pal.inkSoft}; opacity: 1; }

        @media (prefers-reduced-motion: reduce) {
          .jd-accent, .jd-cell-btn, .jd-similar-cell, .jd-index-item button { transition: none; }
        }

        @media (max-width: 1080px) {
          /* The title block drops under the masthead rather than shrinking
             past the point where a salary fits on one line. */
          .jd-masthead { grid-template-columns: 1fr !important; gap: 32px !important; }
          .jd-action { max-width: 420px; }
        }

        @media (max-width: 1024px) {
          /* The index leaves the margin and becomes a rule of tabs across
             the top of the matter it indexes. */
          .jd-body-grid { grid-template-columns: 1fr !important; gap: 28px !important; padding-top: 36px !important; }
          .jd-index { position: static !important; border-right: none !important; padding-right: 0 !important; border-bottom: 1px solid ${pal.rule}; }
          .jd-index-head { display: none; }
          .jd-index-list { display: flex; overflow-x: auto; }
          .jd-index-item { border-top: none !important; flex: 0 0 auto; }
          .jd-index-item button { border-left: none !important; border-bottom: 2px solid transparent; padding: 4px 16px 10px 0 !important; white-space: nowrap; }
          .jd-index-item button[aria-current] { border-bottom-color: ${pal.accent}; }
          .jd-index-apply { display: none !important; }
        }

        @media (max-width: 900px) {
          .jd-cover-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
          .jd-similar-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
        }

        @media (max-width: 768px) {
          .jd-mobile-apply { display: flex !important; }
          .jd-institution-grid { grid-template-columns: 1fr !important; }
          /* The bar sits over the foot of the sheet; give the matter room
             to clear it. */
          .jd-root { padding-bottom: 76px; }
        }

        @media (max-width: 560px) {
          .jd-cover-grid { grid-template-columns: 1fr !important; }
          .jd-similar-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </SheetGround>
  );
};

export default JobDetailsPage;
